import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DesignTokens from '../../styles/DesignTokens';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import useItineraryGenerationSocket from '../../hooks/useItineraryGenerationSocket';
import ItineraryGenerationProgress from './ItineraryGenerationProgress';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${DesignTokens.spacing.lg};
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: ${GlassmorphismTokens.blur.sm};
    -webkit-backdrop-filter: ${GlassmorphismTokens.blur.sm};
  }
`;

const ModalContent = styled(motion.div)`
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius.xl};
  max-width: 600px;
  width: 100%;
  padding: ${DesignTokens.spacing.xl};
  position: relative;
  z-index: 1;
  color: white;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(30, 40, 60, 0.92);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${DesignTokens.spacing.lg};
  right: ${DesignTokens.spacing.lg};
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: ${DesignTokens.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const ErrorContainer = styled.div`
  padding: ${DesignTokens.spacing.lg};
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: ${DesignTokens.borderRadius.lg};
  margin-top: ${DesignTokens.spacing.lg};
`;

const ErrorTitle = styled.h3`
  color: #FCA5A5;
  font-size: ${DesignTokens.typography.fontSize.lg};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
`;

const ErrorMessage = styled.p`
  color: #FECACA;
  font-size: ${DesignTokens.typography.fontSize.base};
  margin: 0;
`;

const RetryButton = styled.button`
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.lg};
  background: ${GlassmorphismTokens.gradient.blueCTA};
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    background: ${GlassmorphismTokens.gradient.blueCTAHover};
    transform: translateY(-2px);
  }
`;

/**
 * Modal component for itinerary generation
 * Displays progress, skeleton preview, and handles completion/errors
 */
const ItineraryGenerationModal = ({ jobId, estimatedDuration = { min: 30, max: 60 }, isSurprise, onClose }) => {
  const navigate = useNavigate();
  const {
    progress,
    skeleton, // eslint-disable-line no-unused-vars
    rotatingMessage,
    isComplete,
    error,
    itineraryId,
    isConnected
  } = useItineraryGenerationSocket(jobId);

  // Redirect to itinerary page when complete
  useEffect(() => {
    if (isComplete && itineraryId && !error) {
      setTimeout(() => {
        navigate(`/itineraries/${itineraryId}`);
        if (onClose) onClose();
      }, 2000); // Wait 2 seconds to show completion
    }
  }, [isComplete, itineraryId, error, navigate, onClose]);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => {
          if (e.target === e.currentTarget && isComplete) {
            if (onClose) onClose();
          }
        }}
      >
        <ModalContent
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          {isComplete && (
            <CloseButton onClick={onClose}>
              ✕
            </CloseButton>
          )}

          <ItineraryGenerationProgress
            progress={progress}
            rotatingMessage={rotatingMessage}
            estimatedDuration={estimatedDuration}
            country={skeleton?.country}
            isSurprise={isSurprise}
          />

          {error && (
            <ErrorContainer>
              <ErrorTitle>Generation Failed</ErrorTitle>
              <ErrorMessage>{error}</ErrorMessage>
              <RetryButton onClick={handleRetry}>
                Try Again
              </RetryButton>
            </ErrorContainer>
          )}

          {!isConnected && !error && (
            <ErrorContainer>
              <ErrorTitle>Connection Issue</ErrorTitle>
              <ErrorMessage>
                Having trouble connecting to the server. Please check your internet connection.
              </ErrorMessage>
            </ErrorContainer>
          )}
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default ItineraryGenerationModal;
